/**
 * @file Search page results that follow the field while the visitor types (sections/main-search.liquid).
 * Owner, 2026-09-21: "when i search product and enter a search page when i click on search it doesnt
 * show product in real time".
 *
 * <bk-live-search data-section-id> wraps the page's search form.
 * - Typing (2 characters or more, debounced 300 ms) renders the same section for the new terms
 *   through the Section Rendering API and swaps [data-live-search-region] (results, filters, sort,
 *   pagination or the no-results state) and the h1 ([data-live-search-title]). The field itself is
 *   never replaced, so focus, the caret and the phone keyboard stay where they are.
 * - The address bar follows (history.replaceState), so a refresh or a shared link shows the same
 *   results. Filters from the previous terms are dropped, as a normal new search would.
 * - Enter / the Search button render at once instead of reloading the page, then close the phone
 *   keyboard. If the request fails, the form falls back to a normal page load.
 * - One request at a time (AbortController); responses are cached per URL for the visit.
 * - The h1 text is repeated in a polite status so screen readers hear the new outcome.
 */
import { debounce, withTimeout } from '@theme/utils';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

class BkLiveSearch extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} */
  #request = null;

  /** @type {Map<string, Document>} */
  #cache = new Map();

  #rendered = '';

  #debounced = debounce(async () => {
    const ok = await this.#update();
    if (!ok) this.#setStale(true);
  }, DEBOUNCE_MS);

  connectedCallback() {
    const form = this.#form();
    const input = this.#input();
    if (!form || !input) return;

    this.#rendered = this.#url().search;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    input.addEventListener('input', () => this.#onInput(), { signal });
    form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#request?.abort();
    this.#request = null;
    this.#debounced.cancel();
  }

  /** @returns {HTMLFormElement | null} */
  #form() {
    const form = this.querySelector('form');
    return form instanceof HTMLFormElement ? form : null;
  }

  /** @returns {HTMLInputElement | null} */
  #input() {
    const input = this.querySelector('input[name="q"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** The search page URL for what the form holds now. */
  #url() {
    const form = this.#form();
    const url = new URL(form?.action || '/search', window.location.origin);
    if (!form) return url;
    for (const [name, value] of new FormData(form)) {
      if (typeof value === 'string') url.searchParams.append(name, name === 'q' ? value.trim() : value);
    }
    return url;
  }

  #onInput() {
    const terms = this.#input()?.value.trim() ?? '';
    this.#setStale(false);
    if (terms.length < MIN_CHARS) {
      this.#debounced.cancel();
      return;
    }
    this.#debounced();
  }

  /** @param {SubmitEvent} event */
  async #onSubmit(event) {
    const terms = this.#input()?.value.trim() ?? '';
    if (!terms) return;
    event.preventDefault();
    this.#debounced.cancel();
    this.#input()?.blur();
    const ok = await this.#update();
    if (!ok) window.location.assign(this.#url().toString());
  }

  /** @returns {Promise<boolean>} false when the results could not be loaded */
  async #update() {
    const url = this.#url();
    if (url.search === this.#rendered) return true;

    const region = document.querySelector('[data-live-search-region]');
    if (!(region instanceof HTMLElement)) return false;

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    region.setAttribute('aria-busy', 'true');

    try {
      const key = url.toString();
      let html = this.#cache.get(key);
      if (!html) {
        const target = new URL(url);
        target.searchParams.set('section_id', this.dataset.sectionId || '');
        const response = await fetch(target, { signal: withTimeout(request.signal), headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Live search: HTTP ${response.status}`);
        html = new DOMParser().parseFromString(await response.text(), 'text/html');
        this.#cache.set(key, html);
      }

      // The visitor kept typing while this loaded: a newer request owns the screen.
      if (this.#request !== request || this.#url().toString() !== key) return true;

      const nextRegion = html.querySelector('[data-live-search-region]');
      if (!nextRegion) throw new Error('Live search: section markup missing');
      region.replaceChildren(...[...nextRegion.childNodes].map((node) => document.importNode(node, true)));

      const title = html.querySelector('[data-live-search-title]')?.textContent?.trim() ?? '';
      const currentTitle = document.querySelector('[data-live-search-title]');
      if (currentTitle && title) currentTitle.textContent = title;
      this.#announce(title);

      this.#rendered = url.search;
      history.replaceState(history.state, '', url.toString());
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return true;
      return false;
    } finally {
      this.#setStale(false);
      if (this.#request === request) {
        this.#request = null;
        region.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * The results on screen are older than the field: say so, and dim them.
   * @param {boolean} stale
   */
  #setStale(stale) {
    const note = this.querySelector('[data-live-search-error]') ?? document.querySelector('[data-live-search-error]');
    if (note instanceof HTMLElement) note.hidden = !stale;
    const region = document.querySelector('[data-live-search-region]');
    if (region instanceof HTMLElement) region.toggleAttribute('data-live-search-stale', stale);
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-live-search-status]');
    if (!status) return;
    status.textContent = '';
    if (!message) return;
    setTimeout(() => {
      status.textContent = message;
    }, 60);
  }
}

if (!customElements.get('bk-live-search')) customElements.define('bk-live-search', BkLiveSearch);
